import React, { useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Button,
  Tooltip,
  Fade,
  Slide,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsNone,
  Chat,
  Person,
  Group,
  Info,
  Warning,
  Error,
  CheckCircle,
  Clear,
  Settings,
  MarkEmailRead,
  MarkEmailUnread,
} from '@mui/icons-material';

function ChatNotificationCenter({
  open,
  onClose,
  notifications = [],
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onClearAll,
}) {
  const [filter, setFilter] = useState('all'); // all, unread, mentions

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'unread') return !notification.read;
    if (filter === 'mentions') return notification.type === 'mention';
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;
  const mentionCount = notifications.filter((n) => n.type === 'mention').length;

  const getNotificationIcon = (type, priority) => {
    switch (type) {
      case 'message':
        return <Chat color="primary" />;
      case 'mention':
        return <Person color="warning" />;
      case 'group':
        return <Group color="info" />;
      case 'system':
        return <Info color="default" />;
      case 'error':
        return <Error color="error" />;
      case 'success':
        return <CheckCircle color="success" />;
      default:
        return <Notifications color="default" />;
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error.main';
      case 'medium':
        return 'warning.main';
      case 'low':
        return 'info.main';
      default:
        return 'text.primary';
    }
  };

  const defaultNotifications = [
    {
      id: 1,
      type: 'message',
      title: 'New message from AI Assistant',
      message: 'I can help you with your questions about machine learning.',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      read: false,
      priority: 'medium',
      sender: 'AI Assistant',
      avatar: null,
    },
    {
      id: 2,
      type: 'mention',
      title: 'You were mentioned',
      message: 'User mentioned you in a conversation about React development.',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      read: true,
      priority: 'high',
      sender: 'John Doe',
      avatar: 'JD',
    },
    {
      id: 3,
      type: 'system',
      title: 'System Update',
      message: 'New features have been added to the chat interface.',
      timestamp: new Date(Date.now() - 60 * 60 * 1000),
      read: false,
      priority: 'low',
      sender: 'System',
      avatar: null,
    },
  ];

  const displayNotifications = notifications.length > 0 ? filteredNotifications : defaultNotifications;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: 360,
          bgcolor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2,
        }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={onMarkAllAsRead}>
                <MarkEmailRead />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear all">
              <IconButton size="small" onClick={onClearAll}>
                <Clear />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={`All (${notifications.length || defaultNotifications.length})`}
            size="small"
            color={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Unread (${unreadCount})`}
            size="small"
            color={filter === 'unread' ? 'primary' : 'default'}
            onClick={() => setFilter('unread')}
            variant={filter === 'unread' ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Mentions (${mentionCount})`}
            size="small"
            color={filter === 'mentions' ? 'primary' : 'default'}
            onClick={() => setFilter('mentions')}
            variant={filter === 'mentions' ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>

      {/* Notifications List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {displayNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsNone sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {displayNotifications.map((notification, index) => (
              <Fade in timeout={300 + index * 100} key={notification.id}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  secondaryAction={(
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!notification.read && (
                        <Tooltip title="Mark as read">
                          <IconButton
                            size="small"
                            onClick={() => onMarkAsRead(notification.id)}
                          >
                            <MarkEmailUnread sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => onDeleteNotification(notification.id)}
                        >
                          <Clear sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        !notification.read ? (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                            }}
                          />
                        ) : null
                      }
                    >
                      {notification.avatar ? (
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {notification.avatar}
                        </Avatar>
                      ) : (
                        <Box sx={{
                          width: 32, height: 32, display: 'flex', alignItems: 'center',
                        }}
                        >
                          {getNotificationIcon(notification.type, notification.priority)}
                        </Box>
                      )}
                    </Badge>
                  </ListItemIcon>

                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            color: getNotificationColor(notification.priority),
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {notification.priority === 'high' && (
                          <Chip
                            label="High"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.625rem', height: 16 }}
                          />
                        )}
                      </Box>
                    )}
                    secondary={(
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontWeight: notification.read ? 400 : 500,
                            mb: 0.5,
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.sender}
                          {' '}
                          •
                          {notification.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    )}
                  />
                </ListItem>
              </Fade>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<Settings />}
          onClick={() => {
            // Open notification settings
          }}
        >
          Notification Settings
        </Button>
      </Box>
    </Drawer>
  );
}

export default ChatNotificationCenter;
